import type {
  CustomerHistoryEvent,
  CustomerHistoryEventType,
  CustomerHistorySnapshot
} from "../types";

export type CustomerHistoryCategory =
  | "package"
  | "session"
  | "cancellation"
  | "payment"
  | "correction";

export interface CustomerHistoryPresentationEvent extends CustomerHistoryEvent {
  category: CustomerHistoryCategory;
  balanceLabel?: string;
  balanceDetail?: string;
}

export interface CustomerHistoryPresentation {
  customer: CustomerHistorySnapshot["customer"];
  stats: {
    packageCount: number;
    purchasedSessions: number;
    usedSessions: number;
    paidAmount: number;
    outstandingAmount: number;
  };
  packages: Array<CustomerHistorySnapshot["packages"][number] & {
    expandedByDefault: boolean;
    events: CustomerHistoryPresentationEvent[];
  }>;
}

const workshopSessionEventTypes = new Set<string>([
  "WORKSHOP_CONSUMED",
  "WORKSHOP_REFUNDED"
]);

function isWorkshopSessionEvent(type: CustomerHistoryEventType): boolean {
  return workshopSessionEventTypes.has(String(type));
}

function categoryFor(type: CustomerHistoryEventType): CustomerHistoryCategory {
  if (type === "PACKAGE_PURCHASED") return "package";
  if (type === "PAYMENT_RECEIVED") return "payment";
  if (isWorkshopSessionEvent(type)) return "session";
  if (type === "SESSION_CANCELLED" || type === "SESSION_NO_SHOW" || type === "CANCELLATION_USED") {
    return "cancellation";
  }
  if (type === "CORRECTION" || type === "SESSION_ADJUSTMENT" || type === "RIGHTS_EXPIRED") {
    return "correction";
  }
  return "session";
}

function presentEvent(event: CustomerHistoryEvent): CustomerHistoryPresentationEvent {
  const category = categoryFor(event.type);
  let detail = event.detail;
  let balanceLabel: string | undefined;
  let balanceDetail: string | undefined;

  if (
    event.type !== "PACKAGE_PURCHASED" &&
    event.beforeSessions !== undefined &&
    event.afterSessions !== undefined
  ) {
    if (isWorkshopSessionEvent(event.type)) {
      balanceLabel = `${event.beforeSessions} → ${event.afterSessions} hak`;
    } else if (event.type === "SESSION_COMPLETED") {
      balanceLabel = `${event.afterSessions} seans kaldı`;
      balanceDetail = `${event.beforeSessions}’den ${event.afterSessions}’ye düştü`;
    } else {
      balanceLabel = `${event.beforeSessions} → ${event.afterSessions} seans`;
    }
  }
  if (
    (event.type === "SESSION_CANCELLED" || event.type === "CANCELLATION_USED") &&
    event.beforeCancellations !== undefined &&
    event.afterCancellations !== undefined
  ) {
    balanceLabel = `İptal hakkı ${event.beforeCancellations} → ${event.afterCancellations}`;
  }
  if (event.type === "SESSION_CANCELLED" && !detail) {
    detail = event.cancellationOutcome === "allowance"
      ? "İptal hakkından düşüldü"
      : event.cancellationOutcome === "session"
        ? "Seans hakkından düşüldü"
        : event.cancellationOutcome === "protected"
          ? "Haklar korundu"
          : "Paket süresi dolduğu için hak kullanılamadı";
  }

  return { ...event, category, detail, balanceLabel, balanceDetail };
}

export function buildCustomerHistoryPresentation(
  snapshot: CustomerHistorySnapshot
): CustomerHistoryPresentation {
  const hasActivePackage = snapshot.packages.some(
    (item) => item.statusCode === "ACTIVE" && item.isOperational
  );
  const packages = [...snapshot.packages]
    .sort((left, right) => {
      const leftActive = left.statusCode === "ACTIVE" && left.isOperational ? 1 : 0;
      const rightActive = right.statusCode === "ACTIVE" && right.isOperational ? 1 : 0;
      return rightActive - leftActive || right.purchasedOn.localeCompare(left.purchasedOn);
    })
    .map((item, index) => {
      const purchaseEvent: CustomerHistoryEvent = {
        id: `purchase-${item.id}`,
        packageId: item.id,
        type: "PACKAGE_PURCHASED",
        occurredAt: `${item.purchasedOn}T00:00:00+03:00`,
        title: "Paket satın alındı",
        detail: `${item.quantity} seans · ₺${item.price.toLocaleString("tr-TR")}`,
        beforeSessions: item.quantity,
        afterSessions: item.quantity
      };
      const events = [
        purchaseEvent,
        ...snapshot.events.filter((event) => event.packageId === item.id)
      ]
        .sort((left, right) => left.occurredAt.localeCompare(right.occurredAt))
        .map(presentEvent);
      return {
        ...item,
        expandedByDefault:
          (item.statusCode === "ACTIVE" && item.isOperational) ||
          (!hasActivePackage && index === 0),
        events
      };
    });

  return {
    customer: snapshot.customer,
    stats: {
      packageCount: snapshot.packages.length,
      purchasedSessions: snapshot.packages.reduce((sum, item) => sum + item.quantity, 0),
      usedSessions: snapshot.packages.reduce(
        (sum, item) => sum + (
          item.usedSessions ?? Math.max(0, item.quantity - item.remainingSessions)
        ),
        0
      ),
      paidAmount: snapshot.packages.reduce((sum, item) => sum + item.paidAmount, 0),
      outstandingAmount: snapshot.packages.reduce((sum, item) => sum + item.remainingAmount, 0)
    },
    packages
  };
}
